/**
 * How much a notification matters: **critical** or **info** (F-0206, concept §5b DS-17…DS-24).
 *
 * Jay, 2026-09-18: *"change notifications into info and critical notification. for now only decisions
 * and blockers are critical. everything else is info."* Severity is a second axis beside the existing
 * reason (`direct | ambient`, `notification-preferences.ts`): reason answers *why you are being told*,
 * severity answers *how much it matters*.
 *
 * Jay, D-0449 (**b**, 2026-10-31): a fork of a machine's identity log is **critical** — the deployment
 * showed one machine two histories, which only a compromised or broken deployment does, and its
 * administrators must act. A plain identity mismatch stays info.
 *
 * ⛔ **This is the only place that decides it** (DS-21). Jay's "for now" is explicit, so the mapping is
 * one edit when it changes, and nothing else infers severity from a kind name — every writer asks
 * {@link notificationSeverityFor}, and every reader asks {@link isCriticalNotification}. A second copy
 * of a policy table is a policy that drifts: I-0436 shipped three orders for one roadmap band list.
 *
 * ⛔ **Severity is policy, not a preference** (DS-24). It is Jay's call for everyone, and it is not a
 * third axis on the preferences grid: that grid is refused unless totally specified, so widening it
 * would make every stored preference blob partial and silently reset every actor to the defaults.
 */

import { DECISION_ITEM_TYPE_KEY, DECISION_STATUS } from './questions.js';

export const NOTIFICATION_SEVERITIES = ['critical', 'info'] as const;
export type NotificationSeverityType = (typeof NOTIFICATION_SEVERITIES)[number];

/** The stored value a query filters on, so no reader spells it out for itself. */
export const CRITICAL_NOTIFICATION_SEVERITY: NotificationSeverityType = 'critical';

/** The workflow status an item enters when something is blocking it (`workflow-presets.ts`). */
export const BLOCKED_STATUS_KEY = 'blocked';

/** What a writer knows about the event when it asks. */
export interface INotificationSeverityFacts {
  /** The event kind — `responsible_changed`, `state_changed`, … or a non-item kind. */
  kind: string;
  /** Whether it is addressed to the reader (named, or made responsible) rather than merely watched. */
  direct: boolean;
  /** The item it is about, when there is one. */
  subjectItemTypeKey?: string | null;
  /** That item's status **after** the event. */
  subjectStatusKey?: string | null;
  /**
   * A decision its **current responsible has declined** (F-0208): still open, but waiting on whoever
   * asked, not on the responsible — so the alarm it raised for them is over (I-0513, DS-69). Absent
   * means not refused.
   */
  subjectRefusedByResponsible?: boolean;
  /**
   * For a `move_requested` notification: whether the held move it announced still waits (F-0275
   * GV-2e). Absent means it still waits — the request is what the notification was written for.
   */
  subjectMoveRequestPending?: boolean;
  /**
   * For an `agent_creation_requested` notification: whether the request still waits (I-1067). Absent
   * means it still waits — the request is what the notification was written for.
   */
  subjectAgentRequestPending?: boolean;
  /**
   * For an `access_requested` notification: whether the request still waits (F-0713). Absent means
   * it still waits — the request is what the notification was written for.
   */
  subjectAccessRequestPending?: boolean;
  /**
   * For a `delivery_fault` notification: whether the fault it announced still holds (T-0600). Absent
   * means it still holds — the fault is what the notification was written for.
   */
  subjectDeliveryFaultOpen?: boolean;
  /**
   * For an `agent_blocked` notification: whether any block it names is still open (F-0751 BA-1).
   * Absent means open — the block is what the notification was written for.
   */
  subjectAgentBlockOpen?: boolean;
  /**
   * For a `machine_identity_mismatch` notification: what the machine's watch saw (its `data.state`),
   * `fork` when the identity log showed it two histories (F-0714 RLY-128, D-0449).
   */
  subjectMachineIdentityState?: string | null;
  /**
   * For a `task_urgent` notification: whether its item is still urgent and open (F-0815 UTN-3).
   * Absent means it is — the raise is what the notification was written for.
   */
  subjectItemUrgent?: boolean;
}

/**
 * The policy, as of 2026-09-18.
 *
 * **Critical** is something addressed to you that is waiting on you:
 * - **a decision given to you** — a responsible change, addressed to you, on a decision that is still
 *   open and that you have not declined. An answered, superseded or withdrawn (I-0473) decision is not
 *   waiting on anyone, and a refused one (F-0208) is waiting on its asker, so the same event on one is
 *   info; and a comment on a decision you merely watch is info, or 38 open decisions would turn every
 *   conversation on them into an alarm;
 * - **a blocker** — your item entering `blocked` (the decision recorded in concept §5b: a `blocks`
 *   edge is *not* the trigger — filing one decision that blocks four items would raise four alarms on
 *   top of the one its responsible already gets);
 * - **an agent's move held for you** — a `move_requested` addressed to you, while the request is
 *   pending (F-0275 GV-2e): the item waits where it is until someone like you answers;
 * - **an agent creation request you may answer** — an `agent_creation_requested`, while it waits
 *   (I-1067): nothing else tells an approver it exists, and the requester's agent waits on them;
 * - **an access request you may answer** — an `access_requested`, while it waits (F-0713): the
 *   agent cannot work in the work area until someone like you answers;
 * - **a delivery fault** — a `delivery_fault` addressed to an administrator, while the fault holds
 *   (T-0600): something a person sends and expects to arrive has stopped arriving;
 * - **an agent blocked on you** — an `agent_blocked`, while a block it names is open (F-0751): an
 *   agent handed you the one step only you can do, and it cannot go on until you do it;
 * - **an urgent task of yours** — a `task_urgent`, while its item is urgent and open (F-0815,
 *   D-0533 a): its push is time-sensitive, the badge stays the decisions waiting;
 * - **a fork of a machine's identity log** — a `machine_identity_mismatch` whose state is `fork`
 *   (D-0449, F-0714 RLY-128): the deployment showed the machine two histories of its identity.
 *
 * **Info** is everything else.
 *
 * Asked twice per row (I-0513, DS-69): once when the row is written, and again — with the subject's
 * current facts, and `direct` re-derived as *the recipient is the subject's current responsible* —
 * whenever the subject's standing changes, so the stored severity follows the wait it announced. The
 * bell counts the stored column; nothing re-derives severity at read time (concept §5b-iii, call 1).
 */
export function notificationSeverityFor(
  facts: INotificationSeverityFacts,
): NotificationSeverityType {
  if (!facts.direct) return 'info';
  if (
    facts.kind === 'responsible_changed' &&
    facts.subjectItemTypeKey === DECISION_ITEM_TYPE_KEY &&
    facts.subjectStatusKey === DECISION_STATUS.open &&
    !facts.subjectRefusedByResponsible
  ) {
    return 'critical';
  }
  if (facts.kind === 'state_changed' && facts.subjectStatusKey === BLOCKED_STATUS_KEY) {
    return 'critical';
  }
  if (facts.kind === 'move_requested' && facts.subjectMoveRequestPending !== false) {
    return 'critical';
  }
  if (facts.kind === 'agent_creation_requested' && facts.subjectAgentRequestPending !== false) {
    return 'critical';
  }
  if (facts.kind === 'access_requested' && facts.subjectAccessRequestPending !== false) {
    return 'critical';
  }
  if (facts.kind === 'delivery_fault' && facts.subjectDeliveryFaultOpen !== false) {
    return 'critical';
  }
  if (facts.kind === 'agent_blocked' && facts.subjectAgentBlockOpen !== false) {
    return 'critical';
  }
  if (facts.kind === 'task_urgent' && facts.subjectItemUrgent !== false) {
    return 'critical';
  }
  if (facts.kind === 'machine_identity_mismatch' && facts.subjectMachineIdentityState === 'fork') {
    return 'critical';
  }
  return 'info';
}

/**
 * Whether a stored notification is critical. A row written before severity existed has none, and
 * reads as info: the old build wrote nothing, and nothing it wrote was classified as an alarm.
 */
export function isCriticalNotification(
  notification: { severity?: string | null } | null | undefined,
): boolean {
  return notification?.severity === CRITICAL_NOTIFICATION_SEVERITY;
}
